import { displayFullName } from '@/lib/utils';
import { buildDefaultValues } from '@/pages/homeInspection/hiAttemptHelpers';
import type { HiAttemptFormValues, HiAttemptVacancyChecklistValues } from '@/pages/homeInspection/hiAttempt.schema';
import type {
  HomeInspectionAttemptPsView,
  HomeInspectionAttemptVacancyChecklist,
  WorkOrderAttempt,
  WorkOrderAttemptListResponse,
} from '@/types/workOrderAttempt.types';
import type { PsFirstAttemptHiFormValues } from './psFirstAttemptHi.schema';
import { adaptPsHiAttemptToHiGetResponse } from './adaptPsHiAttemptToHiGetResponse';

const mapVacancyChecklist = (
  checklist: HomeInspectionAttemptVacancyChecklist | null | undefined
): HiAttemptVacancyChecklistValues => {
  if (!checklist) return {};
  return {
    overgrownOrNeglectedVegetation: checklist.overgrownOrNeglectedVegetation ?? undefined,
    hazardousNoxiousOrUnhealthySubstances: checklist.hazardousNoxiousOrUnhealthySubstances ?? undefined,
    newspapersCircularsFlyersOrMail: checklist.newspapersCircularsFlyersOrMail ?? undefined,
    utilitiesDisconnected: checklist.utilitiesDisconnected ?? undefined,
    junkLitterTrashOrDebris: checklist.junkLitterTrashOrDebris ?? undefined,
    noWindowTreatments: checklist.noWindowTreatments ?? undefined,
    noFurnishingsOrPersonalItems: checklist.noFurnishingsOrPersonalItems ?? undefined,
    neighborStatements: checklist.neighborStatements ?? undefined,
    neighborStatementsSummary: checklist.neighborStatementsSummary ?? undefined,
    boardedOrBrokenWindows: checklist.boardedOrBrokenWindows ?? undefined,
    brokenOrUnlockedDoors: checklist.brokenOrUnlockedDoors ?? undefined,
    riskToHealthSafetyOrWelfare: checklist.riskToHealthSafetyOrWelfare ?? undefined,
    riskToHealthSafetyOrWelfareDetails: checklist.riskToHealthSafetyOrWelfareDetails ?? undefined,
    codeViolationOrVacancyOrder: checklist.codeViolationOrVacancyOrder ?? undefined,
    winterizedByMortgagee: checklist.winterizedByMortgagee ?? undefined,
    writtenStatementOfAbandonment: checklist.writtenStatementOfAbandonment ?? undefined,
    otherIndiciaOfAbandonment: checklist.otherIndiciaOfAbandonment ?? undefined,
    otherIndiciaOfAbandonmentDetails: checklist.otherIndiciaOfAbandonmentDetails ?? undefined,
  };
};

const mapServiceAttempt = (
  ps: WorkOrderAttempt,
  list: WorkOrderAttemptListResponse
): PsFirstAttemptHiFormValues['serviceAttempt'] => {
  const serverName = ps.server
    ? displayFullName(ps.server)
    : ps.vendor?.name
      ? ps.vendor.name
      : list.server
        ? displayFullName(list.server)
        : (list.vendor?.vendor?.name ?? '');

  const date = ps.dateOfAttempt ? new Date(ps.dateOfAttempt) : undefined;

  return {
    attemptId: ps.attemptId,
    workOrderId: ps.workOrderId,
    type: ps.type,
    serverName,
    dateOfAttempt: date,
    timeOfAttempt: date,
    description: ps.description ?? null,
    additionalInformation: ps.additionalInformation ?? null,
    personAccepting: ps.personAccepting ?? null,
    relation: ps.relation ?? null,
    age: ps.age != null ? String(ps.age) : null,
    height: ps.height != null ? String(ps.height).replace('.', "'") : null,
    weight: ps.weight != null ? String(ps.weight) : null,
    hair: ps.hair ?? null,
    sex: ps.sex ?? null,
    race: ps.race ?? null,
    otherDetails: ps.otherDetails ?? null,
    emailAddress: ps.emailAddress ?? null,
    emailSentDate: ps.emailSentDate ? new Date(ps.emailSentDate) : null,
    emailAcknowledgementDate: ps.emailAcknowledgementDate
      ? new Date(ps.emailAcknowledgementDate)
      : null,
    remarks: ps.remarks ?? null,
    trackingNumber: ps.trackingNumber ?? null,
    images:
      ps.workOrderAttemptMedia?.map((media) => ({
        id: media.mediaId,
        name: media.media?.metadata?.fileName ?? 'Image',
        image: null,
        mediaId: media.mediaId,
        mediaData: media.media,
      })) ?? [],
    workOrderMedia: ps.workOrderAttemptMedia,
  };
};

export type PsFirstAttemptHiViewDefaultsInput = {
  listResponse: WorkOrderAttemptListResponse;
  psAttempt: WorkOrderAttempt;
  hiAttempt: HomeInspectionAttemptPsView;
};

/** Map enriched PS attempt GET payload into the shared first-attempt HI form shape (view mode). */
export const mapPsFirstAttemptHiViewDefaults = ({
  listResponse,
  psAttempt,
  hiAttempt,
}: PsFirstAttemptHiViewDefaultsInput): PsFirstAttemptHiFormValues => {
  const hiDefaults: Partial<HiAttemptFormValues> = buildDefaultValues(
    adaptPsHiAttemptToHiGetResponse(hiAttempt)
  );
  const inspectorName = listResponse.server
    ? displayFullName(listResponse.server)
    : (listResponse.vendor?.vendor?.name ?? 'No inspector assigned');

  const values: PsFirstAttemptHiFormValues = {
    serviceAttempt: mapServiceAttempt(psAttempt, listResponse),
    ownerAddressLine1: hiDefaults.ownerAddressLine1,
    ownerAddressLine2: hiDefaults.ownerAddressLine2,
    ownerCity: hiDefaults.ownerCity,
    ownerState: hiDefaults.ownerState,
    ownerZipCode: hiDefaults.ownerZipCode,
    ownerPhone: hiDefaults.ownerPhone,
    maritalStatus: hiDefaults.maritalStatus,
    spouseName: hiDefaults.spouseName,
    ownerOccupied: hiDefaults.ownerOccupied,
    vacant: hiDefaults.vacant,
    bothOwnersResideTogether: hiDefaults.bothOwnersResideTogether,
    coOwnerName: hiDefaults.coOwnerName,
    coOwnerAddressLine1: hiDefaults.coOwnerAddressLine1,
    coOwnerAddressLine2: hiDefaults.coOwnerAddressLine2,
    coOwnerCity: hiDefaults.coOwnerCity,
    coOwnerState: hiDefaults.coOwnerState,
    coOwnerZipCode: hiDefaults.coOwnerZipCode,
    defendantInArmedForces: hiDefaults.defendantInArmedForces,
    tenantOccupied: hiDefaults.tenantOccupied,
    tenants: hiDefaults.tenants ?? [],
    houseExterior: hiDefaults.houseExterior!,
    houseExteriorOther: hiDefaults.houseExteriorOther,
    houseType: hiDefaults.houseType!,
    numberOfUnits: hiDefaults.numberOfUnits ?? 1,
    garage: hiDefaults.garage!,
    sale: hiDefaults.sale!,
    saleBy: hiDefaults.saleBy,
    salePhone: hiDefaults.salePhone,
    saleDate: hiAttempt.saleDate ? new Date(hiAttempt.saleDate) : undefined,
    comments: hiDefaults.comments,
    mediaIds: hiDefaults.mediaIds ?? [],
    inspectorName,
    vacancyChecklist: mapVacancyChecklist(hiAttempt.vacancyChecklist),
  };

  return values;
};
